import { usePostHog } from "posthog-js/react"
import { type ReactNode, useContext, useId, useState } from "react"
import "./tutorials.css"
import { toast } from "sonner"
import { posthogEnabled } from "~/components/providers/Posthog"
import { Badge } from "~/components/ui/badge"
import { Button } from "~/components/ui/button"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { tutorialAnnouncementKey, tutorials } from "~/lib/tutorials"
import { api } from "~/trpc/react"
import { TutorialContext } from "./context"

// One surface for contextual feature discovery and post-login invitations.
// Mount beside the feature, with useTutorial's scope enclosing its controls.
export function TutorialDiscovery({
	tutorialId,
	automatic = false,
	destination,
	layout = "inline",
	children
}: {
	tutorialId: keyof typeof tutorials
	automatic?: boolean
	destination?: string
	layout?: "inline" | "sidebar" | "heading"
	children: ReactNode
}) {
	const coordinator = useContext(TutorialContext)
	const preferences = api.user.getTutorialPreferences.useQuery(undefined, {
		enabled: coordinator.ready
	})
	const [opened, setOpened] = useState(false)
	const [saving, setSaving] = useState(false)
	const titleId = useId()
	const descriptionId = useId()
	const posthog = usePostHog()
	const tutorial = tutorials[tutorialId]
	const announcementKey = tutorialAnnouncementKey(tutorialId)
	const unseen =
		!!preferences.data &&
		!preferences.data.globalHideTutorials &&
		!preferences.data.tutorialHistory[tutorialId]
	const autoOpen =
		automatic &&
		unseen &&
		!coordinator.pending &&
		coordinator.announcement === tutorialId &&
		!coordinator.blocked
	const open = unseen && !coordinator.busy && (opened || autoOpen)

	const respond = async (outcome: "completed" | "dismissed") => {
		if (saving) return
		setSaving(true)
		try {
			// "Not now" closes this invitation without dismissing the tutorial.
			// The login-session guard prevents another automatic invitation on reload.
			if (outcome === "dismissed") coordinator.closeAnnouncement(tutorialId)
			else if (announcementKey)
				await coordinator.acknowledgeAnnouncement(tutorialId, outcome)
			if (
				outcome === "completed" &&
				!coordinator.start(
					tutorialId,
					automatic ? "announcement" : "manual",
					destination
				)
			) {
				toast.info("Finish the current tutorial first.")
				return
			}
			setOpened(false)
			if (posthogEnabled)
				posthog.capture("tutorial_invitation_responded", {
					tutorial_id: tutorialId,
					accepted: outcome === "completed",
					automatic: autoOpen
				})
		} catch {
			toast.error("Couldn't save your response. Please try again.")
		} finally {
			setSaving(false)
		}
	}

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				if (saving) return
				setOpened(next)
				// Click-away/Escape closes the surface, not an implicit "Not now".
				if (!next) coordinator.closeAnnouncement(tutorialId)
			}}
		>
			<span
				data-layout={layout}
				className={`${unseen ? "tutorial-discovery-anchor" : ""} ${layout === "sidebar" ? "tutorial-discovery-sidebar" : ""}`}
			>
				{children}
				{unseen && (
					<PopoverTrigger asChild>
						<Button
							variant="discovery"
							size="icon-touch"
							shape="pill"
							className="tutorial-discovery-trigger"
							aria-label={`Learn about ${tutorial.title.toLowerCase()}`}
							title={`Learn about ${tutorial.title.toLowerCase()}`}
							disabled={!coordinator.ready || coordinator.busy || saving}
						>
							<span aria-hidden="true" className="tutorial-discovery-mark" />
						</Button>
					</PopoverTrigger>
				)}
			</span>
			<PopoverContent
				layout="panel"
				align="end"
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				onOpenAutoFocus={(event) => {
					if (autoOpen && !opened) event.preventDefault()
				}}
			>
				<Badge variant={unseen ? "default" : "secondary"}>
					{unseen ? "New feature" : "Tutorial"}
				</Badge>
				<div className="flex flex-col gap-2">
					<h3 id={titleId} className="text-sm font-semibold text-text-main">
						{tutorial.announcement?.title ?? tutorial.title}
					</h3>
					<p id={descriptionId} className="text-sm text-text-sub">
						{tutorial.announcement?.description ?? tutorial.description}
					</p>
				</div>
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="ghost"
						size="sm"
						disabled={saving}
						onClick={() => void respond("dismissed")}
					>
						Not now
					</Button>
					<Button
						size="sm"
						pending={saving}
						onClick={() => void respond("completed")}
					>
						Check it out
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}
